/**
 * Capability checks. R1 Step 15.
 *
 * The tool runs on the trainee's phone with no developer present, so it
 * diagnoses itself. Every finding names the missing feature and the fix in
 * plain language.
 *
 * Doc 03 section 1.4 is explicit: the app must detect a blocked speech API and
 * display the fix **every time it occurs**. A silent failure is a defect, so a
 * check that cannot run reports that it could not run rather than passing.
 */

export type CapabilityStatus = "blocked" | "ready" | "unknown";

export interface CapabilityFinding {
  /** What the trainee does about it. Empty when nothing is wrong. */
  readonly fix: string;
  readonly id: string;
  readonly name: string;
  readonly status: CapabilityStatus;
  /** Plain language, no jargon, no error codes. */
  readonly detail: string;
}

export interface CapabilityEnvironment {
  readonly localStorageWorks: boolean | null;
  readonly speechSynthesisPresent: boolean;
  /** Null when the voice list has not resolved yet. */
  readonly localVoiceCount: number | null;
  readonly userAgent: string;
}

export type BrowserFamily =
  | "brave"
  | "chromium"
  | "duckduckgo"
  | "firefox"
  | "other";

/**
 * Doc 03 section 1.1 names the targets: Brave primary, Firefox secondary,
 * DuckDuckGo explicitly not a target, and the app must name the two supported
 * browsers when it detects one.
 */
export function browserFamily(userAgent: string): BrowserFamily {
  if (/DuckDuckGo/i.test(userAgent)) return "duckduckgo";
  if (/Brave/i.test(userAgent)) return "brave";
  if (/Firefox|FxiOS/i.test(userAgent)) return "firefox";
  if (/Chrome|Chromium|Edg\//i.test(userAgent)) return "chromium";
  return "other";
}

function browserFinding(userAgent: string): CapabilityFinding {
  const family = browserFamily(userAgent);
  if (family === "duckduckgo") {
    return {
      id: "browser",
      name: "Supported browser",
      status: "blocked",
      detail:
        "DuckDuckGo is a shell around the system web view, not a browser engine, and ATLas cannot run reliably in it.",
      fix: "Open ATLas in Brave or Firefox.",
    };
  }
  if (family === "brave" || family === "firefox") {
    return {
      id: "browser",
      name: "Supported browser",
      status: "ready",
      detail:
        family === "brave"
          ? "Brave, the primary supported browser."
          : "Firefox, the secondary supported browser.",
      fix: "",
    };
  }
  return {
    id: "browser",
    name: "Supported browser",
    status: "unknown",
    detail: "This browser is not one ATLas is tested against.",
    fix: "Brave and Firefox are the two supported browsers. Use one of them if anything misbehaves.",
  };
}

function speechFinding(environment: CapabilityEnvironment): CapabilityFinding {
  const shieldsFix =
    "In Brave, tap the Shields icon beside the address bar and allow fingerprinting for this site, then reload. Shields can switch the speech engine off.";

  if (!environment.speechSynthesisPresent) {
    return {
      id: "speech",
      name: "Speech",
      status: "blocked",
      detail:
        "This browser reports no speech engine, so orders cannot be read aloud.",
      fix:
        browserFamily(environment.userAgent) === "brave"
          ? shieldsFix
          : "Open ATLas in Brave or Firefox, which both provide a speech engine.",
    };
  }
  if (environment.localVoiceCount === null) {
    return {
      id: "speech",
      name: "Speech",
      status: "unknown",
      detail: "The voice list has not arrived yet.",
      fix: "Wait a moment and reload. If it never arrives in Brave, allow this site through Shields.",
    };
  }
  if (environment.localVoiceCount === 0) {
    return {
      id: "speech",
      name: "Speech",
      status: "blocked",
      detail:
        "A speech engine is present but reports no on-device voice. ATLas will not use a network voice.",
      fix: "Install or enable an offline English voice in the device speech settings, then reload.",
    };
  }
  return {
    id: "speech",
    name: "Speech",
    status: "ready",
    detail: `${String(environment.localVoiceCount)} on-device voice${environment.localVoiceCount === 1 ? "" : "s"} available.`,
    fix: "",
  };
}

function storageFinding(environment: CapabilityEnvironment): CapabilityFinding {
  if (environment.localStorageWorks === null) {
    return {
      id: "storage",
      name: "Saving on this device",
      status: "unknown",
      detail: "ATLas could not tell whether this device will keep saved work.",
      fix: "Reload. If it persists, leave private browsing and allow site data for this address.",
    };
  }
  if (!environment.localStorageWorks) {
    return {
      id: "storage",
      name: "Saving on this device",
      status: "blocked",
      detail:
        "This device is refusing to store data, so calibration, fills and notes will be lost on reload.",
      fix: "Leave private browsing, and allow site data for this address in the browser settings.",
    };
  }
  return {
    id: "storage",
    name: "Saving on this device",
    status: "ready",
    detail: "Calibration, fills and notes will be kept on this device.",
    fix: "",
  };
}

export function inspectCapabilities(
  environment: CapabilityEnvironment,
): readonly CapabilityFinding[] {
  return [
    browserFinding(environment.userAgent),
    speechFinding(environment),
    storageFinding(environment),
  ];
}

/** True when anything is blocked, which is what the shell surfaces first. */
export function hasBlockingFinding(
  findings: readonly CapabilityFinding[],
): boolean {
  return findings.some(({ status }) => status === "blocked");
}

/** A one-line summary for the shell header. */
export function capabilitySummary(
  findings: readonly CapabilityFinding[],
): string {
  const blocked = findings.filter(({ status }) => status === "blocked");
  if (blocked.length > 0) {
    return `${String(blocked.length)} thing${blocked.length === 1 ? "" : "s"} will stop ATLas working properly. Each one names its fix below.`;
  }
  const unknown = findings.filter(({ status }) => status === "unknown");
  if (unknown.length > 0) {
    return `${String(unknown.length)} check${unknown.length === 1 ? "" : "s"} could not complete. ATLas will run, but read the note below.`;
  }
  return "All checks passed.";
}
